import { Box, Typography, LinearProgress } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	BudgetConsumption,
	BudgetLimitEnforcementMode,
} from "@/domain/program.interface";

interface BudgetConsumptionBarProps {
	consumption: BudgetConsumption;
	threshold?: number;
	mode?: BudgetLimitEnforcementMode;
}

const money = (n: number): string => `$${Math.round(n).toLocaleString()}`;

type ConsumptionState = "over" | "near" | "ok";

/**
 * SIMS-6076 / SIMS-7605 — read-only budget consumption indicator (Program Maintenance).
 * Matches the Hi-Fi hand-off: "$netEarned / $limit (pct%)", a progress bar with a
 * threshold marker, a $0 / threshold / limit scale row, and a state-based status
 * message + color tint. The net-earned formula caption is rendered by the parent
 * section (SIMS-7605 D7), so this component stays a pure indicator.
 */
export default function BudgetConsumptionBar({
	consumption,
	threshold,
	mode,
}: BudgetConsumptionBarProps): JSX.Element {
	const { netEarned, budgetLimit, percentage } = consumption;
	// SIMS-7605: derive the consumed % from the dollar amounts (authoritative) so the indicator is
	// correct even when the API sends a stale/zero `percentage` (e.g. "$90,000 / $100,000" arriving
	// with percentage=0). Fall back to the provided percentage only when the limit is unknown.
	const pct =
		budgetLimit > 0
			? Math.round((netEarned / budgetLimit) * 100)
			: Math.round(percentage);
	// The bar colour is driven by `pct` vs the threshold, independent of the enforcement mode —
	// RED at/over the threshold, ORANGE in the 10-point approach band, GREEN below. The `pct > 0`
	// guard keeps 0% consumed GREEN even when the threshold is <= 10%.
	const over = threshold != null && pct >= threshold;
	const near = threshold != null && !over && pct > 0 && pct >= threshold - 10;

	const state: ConsumptionState = over ? "over" : near ? "near" : "ok";

	const barColor: "success" | "warning" | "error" = over
		? "error"
		: near
			? "warning"
			: "success";

	const tint = over
		? { bgcolor: "error.light", borderColor: "error.main" }
		: near
			? { bgcolor: "warning.light", borderColor: "warning.main" }
			: { bgcolor: "transparent", borderColor: "divider" };

	// The status message still reflects the enforcement MODE (block denies, warn allows enrollment).
	let statusMessage: string | null = null;
	if (over && mode === "BLOCK") {
		statusMessage = "Threshold reached — new enrollments are blocked.";
	} else if (over) {
		statusMessage = "Threshold reached — enrollment will show a warning.";
	} else if (near && threshold != null) {
		statusMessage = `Approaching threshold — ${threshold - pct}% of budget remaining.`;
	}
	const statusColor = over ? "error.dark" : "warning.dark";

	return (
		<Box
			data-testid="program-budget-consumption"
			data-state={state}
			sx={{
				border: "1px solid",
				borderRadius: 1,
				p: 2,
				...tint,
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					mb: 1,
				}}
			>
				<Typography variant="body2" fontWeight={500}>
					Budget consumption
				</Typography>
				<Typography
					variant="body2"
					data-testid="program-budget-consumption-value"
				>
					<Box component="span" sx={{ fontWeight: 700 }}>
						{money(netEarned)}
					</Box>
					<Box component="span" sx={{ color: "text.secondary" }}>
						{" "}
						/ {money(budgetLimit)} ({pct}%)
					</Box>
				</Typography>
			</Box>

			<Box sx={{ position: "relative", mb: 0.75 }}>
				<LinearProgress
					variant="determinate"
					value={Math.min(pct, 100)}
					color={barColor}
					sx={{ height: 10, borderRadius: 5 }}
				/>
				{threshold != null && (
					<Box
						data-testid="program-budget-consumption-threshold-marker"
						sx={{
							position: "absolute",
							top: "-2px",
							bottom: "-2px",
							left: `${Math.min(threshold, 100)}%`,
							width: "2px",
							bgcolor: "rgba(0, 0, 0, 0.4)",
							borderRadius: "1px",
							transform: "translateX(-50%)",
						}}
					/>
				)}
			</Box>

			<Box
				data-testid="program-budget-consumption-scale"
				sx={{
					display: "flex",
					justifyContent: "space-between",
					color: "text.secondary",
				}}
			>
				<Typography variant="caption">$0</Typography>
				{threshold != null && (
					<Typography variant="caption">{threshold}% threshold</Typography>
				)}
				<Typography variant="caption">{money(budgetLimit)}</Typography>
			</Box>

			{statusMessage && (
				<Box
					data-testid="program-budget-consumption-status"
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						mt: 1.25,
						pt: 1.25,
						borderTop: "1px solid",
						borderColor: tint.borderColor,
						color: statusColor,
					}}
				>
					{over && mode === "BLOCK" ? (
						<BlockIcon fontSize="small" />
					) : (
						<WarningAmberIcon fontSize="small" />
					)}
					<Typography variant="body2" fontWeight={500} color="inherit">
						{statusMessage}
					</Typography>
				</Box>
			)}
		</Box>
	);
}
